import type { UserDetail } from '@src/dto/UserManagement';
import { useEffect, useState } from 'react';
import {
  getAvailableRoles,
  getDisplayRole,
  type UserRole,
} from '../../utils/roleUtils';

interface PresetOption {
  id: string;
  name: string;
}

interface RoleChangeModalProps {
  user: UserDetail | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newRole: UserRole, presetId?: string | null) => void;
  viewerRole: UserRole;
  presets?: PresetOption[];
}

const roleIcon: Record<UserRole, string> = {
  superadmin: '👑',
  admin: '⚡',
  moderator: '🛡️',
  base: '👤',
};

const roleDescription: Record<UserRole, string> = {
  superadmin: 'Full system access',
  admin: 'Administrative privileges',
  moderator: 'Ticket moderation access',
  base: 'Standard user access',
};

export function RoleChangeModal({
  user,
  isOpen,
  onClose,
  onConfirm,
  viewerRole,
  presets,
}: RoleChangeModalProps) {
  const [selectedRole, setSelectedRole] = useState<UserRole>('base');
  const [selectedPresetId, setSelectedPresetId] = useState('');

  useEffect(() => {
    if (isOpen && user) {
      setSelectedRole(getDisplayRole(user.role, viewerRole));
      setSelectedPresetId(user.moderatorPresetId ?? '');
    }
  }, [isOpen, user, viewerRole]);

  if (!isOpen || !user) {
    return null;
  }

  const availableRoles = getAvailableRoles(viewerRole);
  const currentDisplayRole = getDisplayRole(user.role, viewerRole);
  const currentPresetId = user.moderatorPresetId ?? '';
  const showPresetSelector =
    selectedRole === 'moderator' && presets && presets.length > 0;
  const roleChanged = selectedRole !== currentDisplayRole;
  const presetChanged = selectedPresetId !== currentPresetId;
  const hasChanges =
    roleChanged || (selectedRole === 'moderator' && presetChanged);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-neutral-900 rounded-xl max-w-md w-full border border-neutral-800/60 shadow-2xl">
        <div className="px-6 pt-6 pb-4 border-b border-neutral-800/60">
          <h3 className="text-xl font-semibold text-white">Change User Role</h3>
          <p className="text-sm text-neutral-400 mt-2">
            Update role for{' '}
            <span className="text-teal-400 font-medium">{user.email}</span>
          </p>
        </div>

        <div className="p-6 space-y-3">
          {availableRoles.map((role) => (
            <label
              key={role}
              className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                selectedRole === role
                  ? 'border-teal-500/50 bg-teal-500/5'
                  : 'border-neutral-800/60 hover:border-neutral-700 bg-neutral-800/20'
              }`}
            >
              <input
                type="radio"
                name="role"
                value={role}
                aria-label={role}
                checked={selectedRole === role}
                onChange={(e) => setSelectedRole(e.target.value as UserRole)}
                className="sr-only"
              />
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-lg text-xl ${
                  selectedRole === role ? 'bg-teal-500/20' : 'bg-neutral-800/40'
                }`}
              >
                {roleIcon[role]}
              </div>
              <div className="flex-1">
                <div className="text-white font-semibold capitalize">
                  {role}
                </div>
                <div className="text-xs text-neutral-400 mt-0.5">
                  {roleDescription[role]}
                </div>
              </div>
              {selectedRole === role && (
                <div className="size-5 rounded-full bg-teal-500 flex items-center justify-center">
                  <svg
                    className="size-3 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    role="img"
                    aria-label="Selected"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              )}
            </label>
          ))}

          {showPresetSelector && (
            <div className="mt-4 pt-4 border-t border-neutral-800/60">
              <label className="space-y-2">
                <span className="block text-xs font-medium uppercase tracking-wide text-neutral-500">
                  Moderator Preset
                </span>
                <select
                  value={selectedPresetId}
                  onChange={(e) => setSelectedPresetId(e.target.value)}
                  className="w-full rounded-lg border border-neutral-700/70 bg-neutral-800/60 px-3 py-2.5 text-sm text-white transition-colors hover:border-neutral-600 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
                >
                  <option value="">No preset (global fallback)</option>
                  {presets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
        </div>

        <div className="px-6 pb-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm font-medium text-neutral-300 bg-neutral-800/60 hover:bg-neutral-800 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              onConfirm(
                selectedRole,
                selectedRole === 'moderator'
                  ? selectedPresetId || null
                  : undefined,
              )
            }
            disabled={!hasChanges}
            className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Update Role
          </button>
        </div>
      </div>
    </div>
  );
}
